import { useState, useRef, useCallback, useEffect } from 'react';

interface UseAudioPreviewReturn {
  isPlaying: boolean;
  isLoading: boolean;
  currentPreview: string | null;
  playPreview: (url: string, filename: string) => Promise<void>;
  stopPreview: () => void;
}

// Module-level cache for audio blobs - persists across component re-renders
// Key: filename/voice_id, Value: { blob, blobUrl, timestamp }
const audioCache = new Map<string, { blob: Blob; blobUrl: string; timestamp: number }>();
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Get cached audio or return null if not cached/expired
 */
const getCachedAudio = (filename: string): { blob: Blob; blobUrl: string } | null => {
  const cached = audioCache.get(filename);
  if (!cached) return null;

  // Check if cache has expired
  if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
    // Clean up expired entry
    URL.revokeObjectURL(cached.blobUrl);
    audioCache.delete(filename);
    return null;
  }

  return { blob: cached.blob, blobUrl: cached.blobUrl };
};

/**
 * Cache an audio blob
 */
const cacheAudio = (filename: string, blob: Blob): string => {
  // Clean up old entry if exists
  const existing = audioCache.get(filename);
  if (existing) {
    URL.revokeObjectURL(existing.blobUrl);
  }

  const blobUrl = URL.createObjectURL(blob);
  audioCache.set(filename, { blob, blobUrl, timestamp: Date.now() });
  return blobUrl;
};

/**
 * Hook for audio preview functionality
 * Handles fetching, playing, and cleanup of audio previews
 * Includes browser-side caching to reduce server calls
 */
export const useAudioPreview = (maxDuration: number = 15000): UseAudioPreviewReturn => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPreview, setCurrentPreview] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const stopPreview = useCallback(() => {
    // Stop audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;

      // Don't clear src or remove element - just pause and reset
      // This prevents the spurious MEDIA_ELEMENT_ERROR
    }

    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Reset state
    setIsPlaying(false);
    setIsLoading(false);
    setCurrentPreview(null);
  }, []);

  const playPreview = useCallback(async (url: string, filename: string) => {
    try {
      // If same file is playing, stop it
      if (currentPreview === filename && isPlaying) {
        stopPreview();
        return;
      }

      // Stop any existing preview
      stopPreview();

      // Set loading state
      setIsLoading(true);
      setCurrentPreview(filename);

      // Check cache first
      const cached = getCachedAudio(filename);
      let blobUrl: string;

      if (cached) {
        console.log(`🎵 Cache HIT for: ${filename}`);
        blobUrl = cached.blobUrl;
      } else {
        console.log(`🎵 Cache MISS, fetching: ${filename}`);

        // Fetch audio with credentials
        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            'Accept': 'audio/mpeg, audio/*'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        console.log(`🎵 Received blob: ${blob.size} bytes, type: ${blob.type}`);

        if (blob.size === 0) {
          throw new Error('Received empty audio file');
        }

        // Cache the audio and get blob URL
        blobUrl = cacheAudio(filename, blob);
      }

      // Create or reuse audio element
      if (!audioRef.current) {
        audioRef.current = new Audio();
        // Set volume once when creating
        audioRef.current.volume = 1.0; // Start at full volume
        console.log('🎵 Created new Audio element');
      }

      // Set the source and load
      audioRef.current.src = blobUrl;

      // Wait for audio to be ready
      await new Promise<void>((resolve, reject) => {
        const handleCanPlay = () => {
          cleanup();
          resolve();
        };

        const handleError = (e: Event) => {
          cleanup();
          // Only reject on actual load errors, not spurious empty src errors
          const audio = e.target as HTMLAudioElement;
          if (audio.error && audio.error.code !== 4) {
            reject(new Error(`Audio error: ${audio.error.message}`));
          } else {
            // Ignore spurious error 4, still try to play
            resolve();
          }
        };

        const cleanup = () => {
          if (audioRef.current) {
            audioRef.current.removeEventListener('canplaythrough', handleCanPlay);
            audioRef.current.removeEventListener('error', handleError);
          }
        };

        if (audioRef.current) {
          audioRef.current.addEventListener('canplaythrough', handleCanPlay);
          audioRef.current.addEventListener('error', handleError);
          audioRef.current.load();
        }
      });

      // Play the audio
      if (audioRef.current) {
        console.log(`🎵 Starting playback, volume: ${audioRef.current.volume}, muted: ${audioRef.current.muted}`);

        // Ensure volume is set to maximum for testing
        audioRef.current.volume = 1.0;
        audioRef.current.muted = false;

        // IMPORTANT: Try to unmute if the browser auto-muted
        try {
          // Some browsers require user interaction to unmute
          audioRef.current.muted = false;
        } catch (e) {
          console.warn('Could not unmute audio:', e);
        }

        await audioRef.current.play();

        console.log(`🎵 Playback started! State:`, {
          volume: audioRef.current.volume,
          muted: audioRef.current.muted,
          paused: audioRef.current.paused,
          currentTime: audioRef.current.currentTime,
          duration: audioRef.current.duration,
          src: audioRef.current.src.substring(0, 50) + '...'
        });

        setIsPlaying(true);
        setIsLoading(false);

        // Set timeout to stop after max duration
        timeoutRef.current = setTimeout(() => {
          console.log(`🎵 Max duration (${maxDuration}ms) reached, stopping`);
          stopPreview();
        }, maxDuration);

        // Listen for natural end
        audioRef.current.onended = () => {
          console.log('🎵 Audio ended naturally');
          stopPreview();
        };

        // Note: blob URL cleanup is now handled by the cache with TTL expiry
      }

    } catch (error) {
      console.error('🎵 Preview failed:', error);
      stopPreview();
      throw error; // Re-throw so components can show error messages
    }
  }, [currentPreview, isPlaying, stopPreview, maxDuration]);

  // Cleanup on unmount - pause audio but don't revoke cached blob URLs
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        // Note: Don't revoke blob URLs here - they're managed by the cache
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isPlaying,
    isLoading,
    currentPreview,
    playPreview,
    stopPreview
  };
};